
import React from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SummaryPointsProps {
  points: string[] | string;
  onInfoClick: () => void;
}

const SummaryPoints = ({ points, onInfoClick }: SummaryPointsProps) => {
  const { t } = useTranslation();
  // Process the summary points to ensure they're correctly formatted
  const processedPoints = React.useMemo(() => {
    console.log('🔍 Raw summary points received:', points);
    
    // Early return for empty cases
    if (!points) return [];
    
    // Convert string to array if it's a string
    let pointsArray: string[] = [];
    
    if (typeof points === 'string') {
      // If it's a string, split it by line breaks or bullet points
      if (points.trim().length === 0) return [];
      
      // First try to split by newlines or bullet characters (NOT hyphens)
      pointsArray = points
        .split(/\n+|•/)
        .map(line => line.trim())
        .filter(Boolean);
      
      // If we ended up with an empty array but had a non-empty string,
      // use the original string as a single point
      if (pointsArray.length === 0 && points.trim().length > 0) {
        pointsArray = [points.trim()];
      }
    } else if (Array.isArray(points)) {
      // Filter out any null or undefined values and ensure all items are strings
      pointsArray = points
        .filter(point => point !== null && point !== undefined)
        .map(point => String(point))
        .filter(point => point.trim().length > 0);
    }
    
    // Process each point and split long paragraphs into separate bullet points
    const finalPoints: string[] = [];
    
    pointsArray.forEach(point => {
      // Remove bullet character if it already exists at the start
      let cleanPoint = point.trim();
      if (cleanPoint.startsWith('•')) {
        cleanPoint = cleanPoint.substring(1).trim();
      }
      // Only remove dash if it's at the very start (list marker), not hyphens in words
      if (cleanPoint.startsWith('- ')) {
        cleanPoint = cleanPoint.substring(2).trim();
      }
      
      // Add the point as-is without splitting
      if (cleanPoint.length > 0) {
        finalPoints.push(cleanPoint);
      }
    });
    
    console.log('✅ Processed bullet points:', finalPoints);
    return finalPoints.filter(point => point && point.length > 0);
  }, [points]);
  
  // Processing summary points for display
    
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-medium">{t('ai.summaryBy')}</h3>
        <Info 
          className="w-4 h-4 text-gray-400 hover:text-[#ea384c] cursor-pointer transition-colors" 
          onClick={onInfoClick}
        />
      </div>

      {processedPoints && processedPoints.length > 0 ? (
        <ul className="space-y-3 mb-6 list-none">
          {processedPoints.map((point, index) => (
            <li key={index} className="flex items-start gap-2">
              <span className="text-primary mt-1 flex-shrink-0">•</span>
              <span className="flex-1 leading-relaxed">{point}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-500 mb-6">{t('common.noPointsToShow', 'No points to show.')}</p>
      )}
    </>
  );
};

export default SummaryPoints;
